import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Store, QrCode, CheckCircle, Users, DollarSign, 
  ChevronRight, ChevronLeft, X, Smartphone, ShoppingBag,
  ArrowRight, Sparkles, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';

const steps = {
  en: [
    {
      id: 1,
      title: 'Welcome to PeCashGlobal',
      subtitle: 'Your colmado is about to reach families across borders',
      icon: Store,
      content: 'As a PeCashGlobal merchant partner, you can accept purchase authorizations from supporters in the United States for their beneficiaries in the Dominican Republic.',
      tips: [
        'No bank account required to get started',
        'Receive settlements directly to your account',
        'Track all transactions digitally'
      ],
      animation: 'welcome'
    },
    {
      id: 2,
      title: 'Understanding Purchase Authorizations',
      subtitle: 'How the system works',
      icon: ShoppingBag,
      content: 'When a supporter creates a purchase authorization for your colmado, the beneficiary receives a QR code they can use to shop at your store.',
      tips: [
        'Authorizations are specific to your colmado only',
        'Beneficiaries cannot withdraw cash',
        'You provide goods, we handle the payment'
      ],
      animation: 'flow'
    },
    {
      id: 3,
      title: 'Scanning & Validating QR Codes',
      subtitle: 'Process a purchase in seconds',
      icon: QrCode,
      content: 'When a customer shows their QR code, use your dashboard to scan or enter the backup code. You\'ll see the available amount instantly.',
      tips: [
        'Check the authorization amount before providing goods',
        'Partial redemptions are allowed',
        'Always confirm delivery in the app'
      ],
      animation: 'scan'
    },
    {
      id: 4,
      title: 'Confirming Goods Delivery',
      subtitle: 'Complete the transaction',
      icon: CheckCircle,
      content: 'After providing goods to the customer, enter the value delivered and confirm. This releases the payment to your settlement account.',
      tips: [
        'Enter the exact value of goods provided',
        'Multiple redemptions are possible if balance remains',
        'Confirmation is final - double-check amounts'
      ],
      animation: 'confirm'
    },
    {
      id: 5,
      title: 'Receiving Your Settlement',
      subtitle: 'Get paid for your sales',
      icon: DollarSign,
      content: 'Settlements are processed by PeCashGlobal. Track your earnings and payout history in your merchant dashboard.',
      tips: [
        'View real-time settlement amounts',
        'Track all redemption history',
        'Settlements processed regularly'
      ],
      animation: 'payout'
    },
    {
      id: 6,
      title: 'You\'re Ready!',
      subtitle: 'Start accepting authorizations today',
      icon: Sparkles,
      content: 'Your QR code is already set up in your dashboard. Share it with customers or display it in your store.',
      tips: [
        'Print your merchant QR code',
        'Train your staff on the redemption process',
        'Contact support if you need help'
      ],
      animation: 'ready'
    }
  ],
  es: [
    {
      id: 1,
      title: 'Bienvenido a PeCashGlobal',
      subtitle: 'Tu colmado está por llegar a familias más allá de las fronteras',
      icon: Store,
      content: 'Como socio comerciante de PeCashGlobal, puedes aceptar autorizaciones de compra de apoyantes en Estados Unidos para sus beneficiarios en República Dominicana.',
      tips: [
        'No se requiere cuenta bancaria para comenzar',
        'Recibe liquidaciones directamente en tu cuenta',
        'Rastrea todas las transacciones digitalmente'
      ],
      animation: 'welcome'
    },
    {
      id: 2,
      title: 'Entendiendo las Autorizaciones de Compra',
      subtitle: 'Cómo funciona el sistema',
      icon: ShoppingBag,
      content: 'Cuando un apoyante crea una autorización de compra para tu colmado, el beneficiario recibe un código QR que puede usar para comprar en tu tienda.',
      tips: [
        'Las autorizaciones son específicas para tu colmado',
        'Los beneficiarios no pueden retirar efectivo',
        'Tú provees los productos, nosotros manejamos el pago'
      ],
      animation: 'flow'
    },
    {
      id: 3,
      title: 'Escaneando y Validando Códigos QR',
      subtitle: 'Procesa una compra en segundos',
      icon: QrCode,
      content: 'Cuando un cliente muestre su código QR, usa tu panel de control para escanear o ingresar el código de respaldo. Verás el monto disponible al instante.',
      tips: [
        'Verifica el monto de la autorización antes de entregar productos',
        'Se permiten canjes parciales',
        'Siempre confirma la entrega en la app'
      ],
      animation: 'scan'
    },
    {
      id: 4,
      title: 'Confirmando la Entrega de Productos',
      subtitle: 'Completa la transacción',
      icon: CheckCircle,
      content: 'Después de entregar los productos al cliente, ingresa el valor entregado y confirma. Esto libera el pago a tu cuenta de liquidación.',
      tips: [
        'Ingresa el valor exacto de los productos entregados',
        'Son posibles múltiples canjes si queda saldo',
        'La confirmación es final - verifica los montos'
      ],
      animation: 'confirm'
    },
    {
      id: 5,
      title: 'Recibiendo tu Liquidación',
      subtitle: 'Recibe pago por tus ventas',
      icon: DollarSign,
      content: 'Las liquidaciones son procesadas por PeCashGlobal. Rastrea tus ganancias e historial de pagos en tu panel de comerciante.',
      tips: [
        'Ve montos de liquidación en tiempo real',
        'Rastrea todo el historial de canjes',
        'Liquidaciones procesadas regularmente'
      ],
      animation: 'payout'
    },
    {
      id: 6,
      title: '¡Estás Listo!',
      subtitle: 'Comienza a aceptar autorizaciones hoy',
      icon: Sparkles,
      content: 'Tu código QR ya está configurado en tu panel. Compártelo con clientes o muéstralo en tu tienda.',
      tips: [
        'Imprime tu código QR de comerciante',
        'Entrena a tu personal en el proceso de canje',
        'Contacta soporte si necesitas ayuda'
      ],
      animation: 'ready'
    }
  ]
};

const FlowAnimation = ({ type }) => {
  const baseClasses = "w-full h-48 flex items-center justify-center";
  
  const animations = {
    welcome: (
      <motion.div className={baseClasses}>
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5 }}
          className="relative"
        >
          <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg">
            <Store className="w-12 h-12 text-white" />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="absolute -right-8 -top-4"
          >
            <Sparkles className="w-8 h-8 text-amber-500" />
          </motion.div>
        </motion.div>
      </motion.div>
    ),
    flow: (
      <motion.div className={`${baseClasses} gap-4`}>
        <motion.div
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          className="flex flex-col items-center"
        >
          <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
            <Users className="w-8 h-8 text-blue-600" />
          </div>
          <span className="text-xs text-slate-500 mt-2">Supporter</span>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <ArrowRight className="w-6 h-6 text-slate-400" />
        </motion.div>
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3 }}
          className="flex flex-col items-center"
        >
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
            <QrCode className="w-8 h-8 text-emerald-600" />
          </div>
          <span className="text-xs text-slate-500 mt-2">QR Code</span>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <ArrowRight className="w-6 h-6 text-slate-400" />
        </motion.div>
        <motion.div
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="flex flex-col items-center"
        >
          <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
            <Store className="w-8 h-8 text-purple-600" />
          </div>
          <span className="text-xs text-slate-500 mt-2">Colmado</span>
        </motion.div>
      </motion.div>
    ),
    scan: (
      <motion.div className={baseClasses}>
        <motion.div
          animate={{ 
            boxShadow: ['0 0 0 0 rgba(16, 185, 129, 0.4)', '0 0 0 20px rgba(16, 185, 129, 0)']
          }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="relative"
        >
          <div className="w-32 h-32 rounded-2xl bg-white border-4 border-emerald-500 flex items-center justify-center shadow-lg">
            <QrCode className="w-16 h-16 text-emerald-600" />
          </div>
          <motion.div
            animate={{ y: [0, 60, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
            className="absolute top-0 left-0 right-0 h-1 bg-emerald-500 rounded"
          />
        </motion.div>
      </motion.div>
    ),
    confirm: (
      <motion.div className={baseClasses}>
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="relative"
        >
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg"
          >
            <CheckCircle className="w-12 h-12 text-white" />
          </motion.div>
        </motion.div>
      </motion.div>
    ),
    payout: (
      <motion.div className={baseClasses}>
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="flex items-center gap-4"
        >
          <div className="w-20 h-20 rounded-2xl bg-emerald-100 flex items-center justify-center">
            <DollarSign className="w-10 h-10 text-emerald-600" />
          </div>
          <motion.div
            animate={{ x: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          >
            <ArrowRight className="w-8 h-8 text-emerald-500" />
          </motion.div>
          <div className="w-20 h-20 rounded-2xl bg-blue-100 flex items-center justify-center">
            <Store className="w-10 h-10 text-blue-600" />
          </div>
        </motion.div>
      </motion.div>
    ),
    ready: (
      <motion.div className={baseClasses}>
        <motion.div
          animate={{ 
            rotate: [0, 360]
          }}
          transition={{ repeat: Infinity, duration: 20, ease: "linear" }}
          className="absolute w-48 h-48 border-2 border-dashed border-emerald-200 rounded-full"
        />
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring" }}
          className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-xl"
        >
          <Sparkles className="w-12 h-12 text-white" />
        </motion.div>
      </motion.div>
    )
  };
  
  return animations[type] || animations.welcome;
};

export default function MerchantOnboardingGuide({ isOpen, onClose, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const { language } = useLanguage();
  
  const currentSteps = steps[language] || steps.en;
  const step = currentSteps[currentStep];
  const Icon = step.icon;
  
  const handleNext = () => {
    if (currentStep < currentSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete?.();
      onClose?.();
    }
  };
  
  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleSkip = () => {
    onComplete?.();
    onClose?.();
  };
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && onClose?.()}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden"
          data-testid="merchant-onboarding-guide"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4">
            <button
              onClick={onClose}
              className="absolute right-4 top-4 text-white/80 hover:text-white transition-colors"
              data-testid="close-guide-btn"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{step.title}</h2>
                <p className="text-emerald-100 text-sm">{step.subtitle}</p>
              </div>
            </div>
            
            {/* Progress dots */}
            <div className="flex justify-center gap-2 mt-4">
              {currentSteps.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentStep(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentStep 
                      ? 'bg-white w-6' 
                      : idx < currentStep 
                        ? 'bg-white/60' 
                        : 'bg-white/30'
                  }`}
                  data-testid={`step-dot-${idx}`}
                />
              ))}
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6">
            {/* Animation */}
            <FlowAnimation type={step.animation} />
            
            {/* Description */}
            <p className="text-slate-600 text-center mb-6">{step.content}</p>
            
            {/* Tips */}
            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-600" />
                {language === 'es' ? 'Puntos Clave' : 'Key Points'}
              </h4>
              <ul className="space-y-2">
                {step.tips.map((tip, idx) => (
                  <motion.li
                    key={idx}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="flex items-start gap-2 text-sm text-slate-600"
                  >
                    <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {tip}
                  </motion.li>
                ))}
              </ul>
            </div>
            
            {/* Navigation */}
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={handleSkip}
                className="text-slate-500"
                data-testid="skip-guide-btn"
              >
                {language === 'es' ? 'Saltar' : 'Skip'}
              </Button>
              
              <div className="flex gap-2">
                {currentStep > 0 && (
                  <Button
                    variant="outline"
                    onClick={handlePrev}
                    data-testid="prev-step-btn"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    {language === 'es' ? 'Anterior' : 'Back'}
                  </Button>
                )}
                <Button
                  onClick={handleNext}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="next-step-btn"
                >
                  {currentStep === currentSteps.length - 1 
                    ? (language === 'es' ? 'Comenzar' : 'Get Started')
                    : (language === 'es' ? 'Siguiente' : 'Next')}
                  {currentStep < currentSteps.length - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
